import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { containerService } from '../../services/containerService';
import { Package, Ship, Calendar, MapPin, Search, Plus, Filter } from 'lucide-react';

export default function ContainerManagementDashboard() {
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadContainers();
  }, [statusFilter]);

  const loadContainers = async () => {
    try {
      setLoading(true);
      const data = await containerService?.getAll({ 
        status: statusFilter || undefined,
        search: searchTerm || undefined 
      });
      setContainers(data);
      setError('');
    } catch (err) {
      setError(err?.message);
      console.error('Error loading containers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadContainers();
  };

  const handleViewDetails = (containerId) => {
    navigate(`/container-detail-management?id=${containerId}`);
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'draft':
        return `${baseClass} bg-gray-100 text-gray-700`;
      case 'open':
        return `${baseClass} bg-blue-100 text-blue-700`;
      case 'closed':
        return `${baseClass} bg-yellow-100 text-yellow-700`;
      case 'departed':
        return `${baseClass} bg-purple-100 text-purple-700`;
      case 'arrived':
        return `${baseClass} bg-green-100 text-green-700`;
      default:
        return `${baseClass} bg-gray-100 text-gray-700`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des conteneurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Package className="w-8 h-8 text-green-600" />
                Gestion des Conteneurs Maritimes
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez vos conteneurs et leurs envois associés
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Plus className="w-5 h-5 mr-2" />
                Créer un Conteneur
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                  placeholder="Rechercher par code, port, compagnie, navire..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none"
              >
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="open">Ouvert</option>
                <option value="closed">Fermé</option>
                <option value="departed">Parti</option>
                <option value="arrived">Arrivé</option>
              </select>
              <Filter className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
          </div>

          <button
            onClick={handleSearch}
            className="mt-4 w-full md:w-auto px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Rechercher
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Containers List */}
        <div className="mt-6 space-y-4">
          {containers?.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun conteneur trouvé</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-green-600 hover:text-green-700 font-medium"
              >
                Créer votre premier conteneur
              </button>
            </div>
          ) : (
            containers?.map((container) => (
              <div
                key={container?.id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  {/* Container Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {container?.containerCode}
                      </h3>
                      <span className={getStatusBadgeClass(container?.status)}>
                        {container?.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {/* Ports */}
                      <div className="flex items-start gap-2">
                        <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Route</p>
                          <p className="text-sm font-medium text-gray-900">
                            {container?.departurePort || 'N/A'} → {container?.destinationPort || 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="flex items-start gap-2">
                        <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Dates</p>
                          <p className="text-sm font-medium text-gray-900">
                            Départ: {formatDate(container?.departureDate)}
                          </p>
                          <p className="text-sm text-gray-500">
                            ETA: {formatDate(container?.etaDate)}
                          </p>
                        </div>
                      </div>

                      {/* Shipping Line */}
                      <div className="flex items-start gap-2">
                        <Ship className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Compagnie maritime</p>
                          <p className="text-sm font-medium text-gray-900">
                            {container?.shippingLine || 'N/A'}
                          </p>
                          {container?.vesselName && (
                            <p className="text-sm text-gray-500">
                              Navire: {container?.vesselName}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Totals */}
                      <div className="flex items-start gap-2">
                        <Package className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500">Envois</p>
                          <p className="text-sm font-medium text-gray-900">
                            {container?.totalShipments || 0} envoi(s)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 md:mt-0 md:ml-6">
                    <button
                      onClick={() => handleViewDetails(container?.id)}
                      className="w-full md:w-auto px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      Voir Détails
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Create Modal Placeholder - Would be implemented as separate component */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Créer un Conteneur</h3>
            <p className="text-gray-600 mb-4">
              Fonctionnalité à implémenter : formulaire de création de conteneur
            </p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}